import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import StudentSidebar from '../components/StudentSidebar';
import AssignedReportCard from '../components/AssignedReportCard';
import PublicAssessmentCard from '../components/PublicAssessmentCard';
import PreviewModal from '../components/PreviewModal';
import EmptyState from '../components/EmptyState';
import { ArrowRightIcon, SearchIcon, SparklesIcon, FileTextIcon, GlobeIcon, ArrowLeftIcon, LayoutDashboardIcon } from 'lucide-react';
// Helper function to calculate status based on progress_pct
const getStudentStatus = (assessment: {
  progress_pct: number;
  dateCompleted?: string;
}) => {
  if (assessment.dateCompleted) return 'Completed';
  if (assessment.progress_pct >= 20) return 'In progress';
  if (assessment.progress_pct > 0 && assessment.progress_pct < 20) return 'Dropped Off Early';
  return 'Not started';
};
export function StudentHome() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'workspaces' | 'assessments'>('assessments');
  const [previewAssessment, setPreviewAssessment] = useState(null);
  const [archivedAssessments, setArchivedAssessments] = useState<number[]>([]);
  const [publicSearchQuery, setPublicSearchQuery] = useState('');
  const [educatorBannerDismissed, setEducatorBannerDismissed] = useState(false);
  // SIMULATED AUTH CONTEXT
  // In production, this would come from your auth provider
  const currentUserRole: 'educator' | 'learner' = 'educator';
  const isEducator = currentUserRole === 'educator';
  const assignedAssessmentsRaw = [{
    id: 1,
    title: 'Math Fundamentals: Fractions',
    mode: 'Challenge',
    questions: 15,
    dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dateAssigned: '2024-01-20',
    dateStarted: '2024-01-21',
    lastInteractedWith: '2024-01-23',
    progress_pct: 45,
    participants: 24,
    icon: '🔢',
    creator: 'Ms. Johnson'
  }, {
    id: 2,
    title: 'Science: States of Matter',
    mode: 'Coach',
    questions: 12,
    dueDate: '2024-01-28',
    dateAssigned: '2024-01-22',
    progress_pct: 0,
    participants: 18,
    icon: '🧪',
    creator: 'Mr. Smith'
  }, {
    id: 3,
    title: 'English: Grammar Basics',
    mode: 'Challenge',
    questions: 20,
    dateAssigned: '2024-01-15',
    dateStarted: '2024-01-16',
    dateCompleted: '2024-01-18',
    lastInteractedWith: '2024-01-18',
    progress_pct: 100,
    participants: 32,
    icon: '📝',
    creator: 'Mrs. Davis',
    score: 92
  }];
  // Calculate status dynamically
  const assignedAssessments = assignedAssessmentsRaw.map(assessment => ({
    ...assessment,
    status: getStudentStatus(assessment)
  }));
  // Count dropped-off assessments (excluding archived)
  const droppedOffCount = assignedAssessments.filter(a => a.status === 'Dropped Off Early' && !archivedAssessments.includes(a.id)).length;
  const publicAssessments = [{
    id: 1,
    title: 'Math Fundamentals: Fractions',
    tags: ['Math', 'Elementary', 'Practice'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 1245,
    icon: '🔢',
    description: 'Master the basics of fractions including addition, subtraction, multiplication, and division.',
    questions: 15,
    duration: '20 min'
  }, {
    id: 2,
    title: 'Science: States of Matter',
    tags: [],
    mode: 'Coach',
    creator: 'Jane Smith',
    attempts: 876,
    icon: '🧪',
    description: 'Explore the three states of matter and their properties through interactive questions.',
    questions: 12,
    duration: '15 min'
  }, {
    id: 3,
    title: 'English: Grammar Basics',
    tags: ['English', 'Grammar'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 1543,
    icon: '📝',
    description: 'Test your knowledge of grammar fundamentals including parts of speech and sentence structure.',
    questions: 20,
    duration: '25 min'
  }, {
    id: 4,
    title: 'History: Ancient Civilizations',
    mode: 'Coach',
    creator: 'John Doe',
    attempts: 654,
    icon: '🏛️',
    description: 'Journey through ancient civilizations and learn about their cultures and achievements.',
    questions: 18,
    duration: '30 min'
  }, {
    id: 5,
    title: 'Geography: Continents and Oceans',
    tags: ['Geography'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 987,
    icon: '🌍',
    description: "Learn about the world's continents and oceans with this comprehensive assessment.",
    questions: 10,
    duration: '15 min'
  }, {
    id: 6,
    title: 'Art History: Renaissance Period',
    tags: ['Art', 'History', 'Culture', 'Renaissance'],
    mode: 'Coach',
    creator: 'Sarah Johnson',
    attempts: 432,
    icon: '🎨',
    description: 'Discover the masterpieces and artists of the Renaissance period.',
    questions: 14,
    duration: '20 min'
  }];
  const handleArchiveToggle = (assessmentId: number) => {
    setArchivedAssessments(prev => prev.includes(assessmentId) ? prev.filter(id => id !== assessmentId) : [...prev, assessmentId]);
  };
  const filteredPublicAssessments = publicAssessments.filter(assessment => {
    if (publicSearchQuery === '') return true;
    return assessment.title.toLowerCase().includes(publicSearchQuery.toLowerCase()) || assessment.creator.toLowerCase().includes(publicSearchQuery.toLowerCase()) || assessment.tags.some(tag => tag.toLowerCase().includes(publicSearchQuery.toLowerCase()));
  });
  return <div className="flex min-h-screen bg-background" data-id="element-3700">
      <div className="hidden md:block" data-id="element-3701">
        <StudentSidebar droppedOffCount={droppedOffCount} data-id="element-3702" />
      </div>
      <div className="flex-1 flex flex-col min-w-0 w-full" data-id="element-3703">
        <Header data-id="element-3704" />
        <main className="flex-1 overflow-y-auto" data-id="element-3705">
          <div className="max-w-6xl mx-auto px-6 py-8" data-id="element-3706">
            {/* Educator Navigation Banner */}
            {isEducator && <motion.div initial={{
            opacity: 0,
            y: -12
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-6 bg-surface-elevated rounded-xl p-4 shadow-sm border border-[#2563EB]/20 border-l-4 border-l-[#2563EB]" data-id="element-3707">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-3708">
                  <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-3709">
                    <div className="w-9 h-9 rounded-lg bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0" data-id="element-3710">
                      <LayoutDashboardIcon size={18} className="text-[#2563EB]" data-id="element-3711" />
                    </div>
                    <div className="min-w-0" data-id="element-3712">
                      <p className="text-sm font-semibold text-text-primary" data-id="element-3713">
                        You're viewing the learner experience
                      </p>
                      <p className="text-xs text-text-secondary mt-0.5" data-id="element-3714">
                        This is what your students see. Switch back to manage
                        assessments and analytics.
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-12 sm:ml-0" data-id="element-3715">
                    <button onClick={() => navigate('/workspace')} className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-sm font-semibold hover:bg-[#1d4ed8] transition-colors shadow-sm" data-id="element-3716">
                      <ArrowLeftIcon size={15} data-id="element-3717" />
                      Educator Dashboard
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Tabs */}
            <div className="flex items-center gap-4 mb-8 border-b border-custom" data-id="element-3718">
              <button onClick={() => setActiveTab('assessments')} className={`px-4 py-3 text-sm font-semibold transition-all relative ${activeTab === 'assessments' ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-3719">
                Assessments
                {activeTab === 'assessments' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" data-id="element-3720" />}
              </button>
              <button onClick={() => setActiveTab('workspaces')} className={`px-4 py-3 text-sm font-semibold transition-all relative ${activeTab === 'workspaces' ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-3721">
                Workspaces
                {activeTab === 'workspaces' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" data-id="element-3722" />}
              </button>
            </div>
            {/* Assessments Tab Content */}
            {activeTab === 'assessments' && <>
                {/* Assigned Assessments Section - Show only 3 */}
                <section className="mb-16" data-id="element-3723">
                  <div className="flex items-center justify-between mb-6" data-id="element-3724">
                    <h2 className="text-2xl font-bold text-text-primary" data-id="element-3725">
                      Assigned Assessments
                    </h2>
                    {assignedAssessments.length > 0 && <button onClick={() => navigate('/student/reports')} className="flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1d4ed8] transition-colors group" aria-label="View all assessments" data-id="element-3726">
                        View All
                        <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform duration-200" data-id="element-3727" />
                      </button>}
                  </div>
                  {assignedAssessments.length === 0 ? <EmptyState icon={<FileTextIcon size={48} className="text-text-tertiary" data-id="element-3729" />} title="No assigned assessments yet" description="Your teacher hasn't assigned any assessments yet. Check back later or explore public assessments below." data-id="element-3728" /> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3730">
                      {assignedAssessments.slice(0, 3).map(assessment => <AssignedReportCard key={assessment.id} report={assessment} isArchived={archivedAssessments.includes(assessment.id)} onArchiveToggle={() => handleArchiveToggle(assessment.id)} data-id="element-3731" />)}
                    </div>}
                </section>
                {/* Divider */}
                {assignedAssessments.length > 0 && <div className="border-t border-custom my-12" data-id="element-3732"></div>}
                {/* Public Assessments Section */}
                <section data-id="element-3733">
                  <div className="flex items-center justify-between mb-6" data-id="element-3734">
                    <h2 className="text-xl font-bold text-text-primary" data-id="element-3735">
                      Browse Public Assessments
                    </h2>
                  </div>
                  {/* Search Bar */}
                  <div className="flex items-center justify-end mb-6" data-id="element-3736">
                    <div className="relative w-full md:w-96" data-id="element-3737">
                      <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary" data-id="element-3738" />
                      <input type="text" placeholder="Search by title, creator, or tags..." value={publicSearchQuery} onChange={e => setPublicSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary placeholder-text-tertiary" data-id="element-3739" />
                    </div>
                  </div>
                  {filteredPublicAssessments.length === 0 ? <EmptyState icon={<GlobeIcon size={48} className="text-text-tertiary" data-id="element-3741" />} title={publicSearchQuery ? 'No assessments found' : 'No public assessments available'} description={publicSearchQuery ? 'Try adjusting your search terms or browse all assessments.' : 'There are no public assessments available at the moment. Check back later!'} actionLabel={publicSearchQuery ? 'Clear Search' : undefined} onAction={publicSearchQuery ? () => setPublicSearchQuery('') : undefined} data-id="element-3740" /> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3742">
                      {filteredPublicAssessments.map(assessment => <PublicAssessmentCard key={assessment.id} assessment={assessment} onPreview={setPreviewAssessment} data-id="element-3743" />)}
                    </div>}
                </section>
              </>}
            {/* Workspaces Tab Content */}
            {activeTab === 'workspaces' && <section data-id="element-3744">
                <div className="flex items-center justify-between mb-6" data-id="element-3745">
                  <h2 className="text-2xl font-bold text-text-primary" data-id="element-3746">
                    Workspaces
                  </h2>
                </div>
                <div className="bg-gradient-to-br from-[#00B75F]/5 via-[#2563EB]/5 to-[#8B5CF6]/5 border-2 border-custom rounded-2xl p-12 text-center relative overflow-hidden" data-id="element-3747">
                  {/* Animated background elements */}
                  <div className="absolute inset-0 opacity-10" data-id="element-3748">
                    <motion.div animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 90, 0]
                }} transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="absolute top-0 right-0 w-64 h-64 bg-[#00B75F] rounded-full blur-3xl" data-id="element-3749" />
                    <motion.div animate={{
                  scale: [1, 1.3, 1],
                  rotate: [0, -90, 0]
                }} transition={{
                  duration: 15,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="absolute bottom-0 left-0 w-64 h-64 bg-[#2563EB] rounded-full blur-3xl" data-id="element-3750" />
                  </div>
                  <div className="max-w-2xl mx-auto relative z-10" data-id="element-3751">
                    <motion.div animate={{
                  y: [0, -10, 0]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="w-20 h-20 bg-gradient-to-br from-[#00B75F] to-[#2563EB] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-3752">
                      <SparklesIcon size={40} className="text-white" data-id="element-3753" />
                    </motion.div>
                    <h3 className="text-3xl font-bold text-text-primary mb-4 bg-gradient-to-r from-[#00B75F] to-[#2563EB] bg-clip-text text-transparent" data-id="element-3754">
                      Workspaces Are Coming Soon
                    </h3>
                    <p className="text-lg text-text-primary font-semibold mb-3" data-id="element-3755">
                      Your new AI-powered learning superpower.
                    </p>
                    <p className="text-text-secondary text-base mb-8" data-id="element-3756">
                      Personalized learning
                    </p>
                    <button disabled className="px-8 py-4 bg-gray-200 text-gray-400 rounded-xl font-semibold cursor-not-allowed opacity-50" data-id="element-3757">
                      Coming Soon
                    </button>
                    <p className="text-xs text-text-tertiary mt-6" data-id="element-3758">
                      There is nothing like it.
                    </p>
                  </div>
                </div>
              </section>}
          </div>
        </main>
      </div>
      {/* Preview Modal */}
      {previewAssessment && <PreviewModal assessment={previewAssessment} onClose={() => setPreviewAssessment(null)} userRole="student" data-id="element-3759" />}
    </div>;
}